import React, { useId, useState } from 'react';
import { Claim } from '../../api/claims';
import { Button } from '../../components/ui';
import { ClaimPicker } from './ClaimPicker';
import type { AssignConfirmation } from './claimRefMatch';
import './lanes.css';

/**
 * Standalone "assign to a claim" modal (Tasks Rebuild Task 12), built on top
 * of `ClaimPicker`. Used for every "pick one claim and confirm" flow that
 * isn't already embedded inside a bigger form: bulk-assign in the
 * Lender/Post/Manual lane bulk bars, and the single-task Assign/Reassign
 * action in `TaskDetailPane`. (`ClientLane`'s per-sender "assign all" modal
 * and `CreateTaskModal`'s inline claim field are their own callers of
 * `ClaimPicker` directly, since they're each already a bigger modal/form and
 * don't need a second nested modal shell.)
 *
 * Wrong-claim attach guard (2026-09-28): callers can pre-fill the search
 * with the email's own reference (`initialTerm`), offer its refs as chips
 * (`suggestions`), show a neutral `note`, and hold the assign behind a
 * confirm (`getConfirmation`) when the picked claim isn't one the email
 * mentions. The confirm replaces the action row until staff choose Back or
 * the confirm button; picking another claim clears it.
 */

export interface ClaimPickerModalProps {
  title: string;
  hint?: React.ReactNode;
  confirmLabel?: string;
  onConfirm: (claim: Claim) => void | Promise<void>;
  onClose: () => void;
  /** Disables the confirm button while a parent-owned action is in flight. */
  processing?: boolean;
  /** Starting search term, e.g. the email's own claim reference. */
  initialTerm?: string;
  /** The email's references as quick-pick chips. */
  suggestions?: string[];
  /** Neutral, non-blocking note shown above the picker. */
  note?: React.ReactNode;
  /** Returns a confirm to show before assigning `claim`, or null to assign
   * straight away. */
  getConfirmation?: (claim: Claim) => AssignConfirmation | null;
}

export const ClaimPickerModal: React.FC<ClaimPickerModalProps> = ({
  title, hint, confirmLabel = 'Assign', onConfirm, onClose, processing,
  initialTerm, suggestions, note, getConfirmation,
}) => {
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<AssignConfirmation | null>(null);
  const titleId = useId();
  const confirmTitleId = useId();

  const handleSelect = (claim: Claim) => {
    setSelectedClaim(claim);
    setPendingConfirm(null);
  };

  const handleAssignClick = () => {
    if (!selectedClaim) return;
    const confirmation = getConfirmation?.(selectedClaim) ?? null;
    if (confirmation) {
      setPendingConfirm(confirmation);
      return;
    }
    onConfirm(selectedClaim);
  };

  return (
    <div className="lane-modal-backdrop" onClick={onClose}>
      <div
        className="lane-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="lane-modal__title" id={titleId}>{title}</h2>
        {hint && <p className="lane-modal__hint">{hint}</p>}
        {note && <div className="lane-modal__note">{note}</div>}
        <ClaimPicker
          selectedClaim={selectedClaim}
          onSelect={handleSelect}
          autoFocus
          initialTerm={initialTerm}
          suggestions={suggestions}
        />
        {pendingConfirm && selectedClaim ? (
          <div
            className={`lane-modal__confirm ${pendingConfirm.tone === 'mismatch' ? 'lane-modal__confirm--mismatch' : ''}`.trim()}
            role="alertdialog"
            aria-labelledby={confirmTitleId}
          >
            <p className="lane-modal__confirm-title" id={confirmTitleId}>{pendingConfirm.title}</p>
            {pendingConfirm.lines.map((line) => (
              <p key={line} className="lane-modal__confirm-line">{line}</p>
            ))}
            <div className="lane-modal__actions">
              {/* Focus starts on Back so a reflex Enter can't attach. */}
              <Button variant="secondary" onClick={() => setPendingConfirm(null)} disabled={processing} autoFocus>
                Back
              </Button>
              <Button
                variant={pendingConfirm.tone === 'mismatch' ? 'danger' : 'primary'}
                disabled={processing}
                onClick={() => onConfirm(selectedClaim)}
              >
                {pendingConfirm.confirmLabel}
              </Button>
            </div>
          </div>
        ) : (
          <div className="lane-modal__actions">
            <Button variant="secondary" onClick={onClose} disabled={processing}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!selectedClaim || processing}
              onClick={handleAssignClick}
            >
              {confirmLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimPickerModal;
